// 2026-04-26 (M80 rewrite): NV — list chương theo vòng học + AI recommend
//
// Layout:
//   [Header] ← Trang chủ | Tiêu đề | Badge "Vòng N"
//   [Banner round status — in_progress/reviewing/recommend]
//   [List modules — mỗi card có icon trạng thái + click vào reader]
//   [Footer — version + nút "Hoàn thành toàn bộ" khi đủ điều kiện]
//
// Trạng thái module (khớp BE getCurrentRoundState):
//   - round.status = 'in_progress' (đang học vòng):
//     • completed = true → ✅ Đã xong vòng này
//     • module = nextModule → ⏳ Học ngay (highlight)
//     • sau nextModule → 🔒 Khoá (phải xong chương trước)
//   - round.status = 'reviewing' (đang ôn chương AI khuyến nghị):
//     • is_recommended && !is_reviewed → 🔄 Cần ôn (highlight)
//     • is_recommended && is_reviewed → ✅ Đã ôn
//     • !is_recommended → đã xong vòng (xám, không click được)

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';

/**
 * Snippet body chương — strip HTML/markdown markers + bỏ [chunk: ...] internal IDs
 * + lấy 2 câu đầu đầy đủ (cắt ở dấu chấm gần nhất khi đủ độ dài).
 */
function snippet(md, n = 180) {
  if (!md) return '';
  let s = String(md);
  // Strip HTML tags
  s = s.replace(/<[^>]+>/g, ' ');
  // Bỏ chunk markers [chunk: xxx] và [xxx]
  s = s.replace(/\[chunk:\s*[^\]]+\]/gi, '');
  s = s.replace(/\[[a-z][a-z0-9_-]+\]/gi, '');
  // Bỏ markdown markers
  s = s.replace(/[#*_`>]/g, '');
  // Collapse whitespace
  s = s.replace(/\s+/g, ' ').trim();
  if (s.length <= n) return s;
  // Cắt ở dấu chấm gần nhất sau ngưỡng 60% n
  const cut = s.slice(0, n);
  const lastDot = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  if (lastDot > n * 0.6) return cut.slice(0, lastDot + 1);
  return cut + '…';
}

/**
 * Trả về { label, cls, icon, clickable, cardVariant } cho module dựa trên round + state.
 * cardVariant định nghĩa visual khác nhau cho mỗi trạng thái — dễ phân biệt khi scroll.
 */
function moduleBadge(m, roundStatus, nextModuleId) {
  if (roundStatus === 'reviewing') {
    if (m.is_recommended && m.is_reviewed) {
      return {
        label: 'Đã ôn xong', icon: '✅',
        cls: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
        clickable: false, cardVariant: 'completed',
      };
    }
    if (m.is_recommended) {
      return {
        label: 'Cần ôn lại', icon: '🔄',
        cls: 'bg-amber-100 text-amber-800 border border-amber-300',
        clickable: true, cardVariant: 'review',
      };
    }
    return {
      label: 'Đã xong vòng cũ', icon: '✓',
      cls: 'bg-slate-100 text-slate-500 border border-slate-200',
      clickable: false, cardVariant: 'locked',
    };
  }
  // round.status = 'in_progress'
  if (m.completed) {
    return {
      label: 'Đã xong vòng này', icon: '✅',
      cls: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
      clickable: true, cardVariant: 'completed',
    };
  }
  if (m.id === nextModuleId) {
    return {
      label: 'Học ngay', icon: '⏳',
      cls: 'bg-sky-100 text-sky-800 border border-sky-300',
      clickable: true, cardVariant: 'next',
    };
  }
  return {
    label: 'Đợi chương trước', icon: '🔒',
    cls: 'bg-slate-100 text-slate-400 border border-slate-200',
    clickable: false, cardVariant: 'locked',
  };
}

const CARD_STYLES = {
  completed: 'bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-md',
  next:      'bg-gradient-to-br from-sky-50 to-indigo-50 border-2 border-sky-300 ring-2 ring-sky-200 shadow-md hover:shadow-lg',
  review:    'bg-amber-50 border-2 border-amber-300 ring-2 ring-amber-200 shadow-md hover:shadow-lg',
  locked:    'bg-slate-50 border border-slate-200 opacity-60',
};

export default function RulesModulesListPage() {
  const user = useAuthStore(s => s.user);
  const nav = useNavigate();
  const [list, setList] = useState(null);          // {modules, version_no}
  const [round, setRound] = useState(null);        // {round, modules, nextModule, allChaptersDone, ...}
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [listRes, roundRes] = await Promise.all([
          api.rulesModulesList(),
          api.rulesM80RoundCurrent(),
        ]);
        if (cancelled) return;
        setList(listRes);
        setRound(roundRes);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 400) {
          toast.error('Cần xác minh L2 trước khi học nội quy.');
          nav('/', { replace: true });
        } else {
          toast.error('Lỗi load: ' + (e?.message || e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [nav]);

  async function handleFinishAll() {
    if (finishing) return;
    const ok = await showConfirm({
      title: 'Hoàn thành toàn bộ nội quy?',
      message:
        'AI sẽ đánh giá lại các câu bạn đã trả lời sai/xem phao nhiều và đề xuất ôn các chương cần củng cố.\n\n' +
        'Sau khi ôn xong (nếu có) sẽ tự mở vòng học mới từ chương 1.',
      confirmText: 'Hoàn thành & xem khuyến nghị',
    });
    if (!ok) return;

    setFinishing(true);
    try {
      const r = await api.rulesM80RoundFinishAll();
      if (r.needsReview) {
        toast.success(`📝 AI đề xuất ôn lại ${r.recommendedModuleIds.length} chương`);
      } else {
        toast.success('🎉 Tất cả câu đều ổn! Vòng học mới đã mở.');
      }
      // Reload state
      const roundRes = await api.rulesM80RoundCurrent();
      setRound(roundRes);
    } catch (e) {
      toast.error('Lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setFinishing(false);
    }
  }

  // Map module info (title, body_md preview) từ list → round.modules
  const modulesWithMeta = (round?.modules || []).map(rm => {
    const meta = list?.modules?.find(m => m.id === rm.id) || {};
    return { ...rm, title: rm.title || meta.title, body_md: meta.body_md };
  });

  const roundStatus = round?.round?.status;
  const roundNo = round?.round?.round_no || 1;
  const allDone = round?.allChaptersDone;
  const nextModuleId = round?.nextModule?.id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-emerald-50">
      <div className="max-w-4xl mx-auto p-4 md:p-6">

        {/* Header */}
        <header className="mb-4 flex items-center gap-3 flex-wrap">
          <Link to="/" className="text-sm px-3 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700">
            ← Về trang chủ
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-sky-700">📚 Học nội quy</h1>
            <p className="text-xs text-slate-500">
              Đọc → AI sinh câu hỏi → trả lời → ôn lại các chương yếu → vòng mới
            </p>
          </div>
          {!loading && round && (
            <div className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-sm font-bold rounded-full shadow-sm">
              Vòng {roundNo}
            </div>
          )}
        </header>

        {loading && <div className="text-sm text-slate-500">Đang tải…</div>}

        {!loading && round && (
          <>
            {/* Banner round status */}
            {roundStatus === 'reviewing' && (
              <div className="mb-4 bg-amber-50 border-2 border-amber-300 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🎯</span>
                  <div className="flex-1">
                    <div className="font-bold text-amber-900 mb-1">
                      Vòng {roundNo} cần ôn lại {round.recommendedModuleIds.length} chương
                    </div>
                    <div className="text-sm text-amber-800">
                      AI đã đánh giá vòng vừa qua: bạn trả lời sai hoặc xem phao nhiều ở các chương được đánh dấu.
                      Hoàn thành ôn các chương đó → tự động mở vòng {roundNo + 1}.
                    </div>
                    <div className="text-xs text-amber-700 mt-2">
                      Đã ôn: <strong>{round.reviewedModuleIds.length}/{round.recommendedModuleIds.length}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {roundStatus === 'in_progress' && allDone && (
              <div className="mb-4 bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4 text-center">
                <div className="text-3xl mb-2">🏆</div>
                <div className="font-bold text-emerald-900 mb-2">Đã học hết các chương vòng {roundNo}!</div>
                <div className="text-sm text-emerald-800 mb-4">
                  Bấm "Hoàn thành toàn bộ" để AI đánh giá và đề xuất ôn các chương cần củng cố trước khi vòng mới.
                </div>
                <button
                  onClick={handleFinishAll}
                  disabled={finishing}
                  className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md disabled:opacity-50"
                >
                  {finishing ? '⏳ AI đang đánh giá...' : '✅ Hoàn thành toàn bộ'}
                </button>
              </div>
            )}

            {/* List modules */}
            {modulesWithMeta.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">
                Hiện chưa có chương nội quy nào. Vui lòng quay lại sau khi chủ shop cập nhật.
              </div>
            ) : (
              <div className="space-y-3">
                {modulesWithMeta.map((m, i) => {
                  const badge = moduleBadge(m, roundStatus, nextModuleId);
                  const variant = badge.cardVariant;
                  const cardClass = `block rounded-2xl transition p-4 ${
                    badge.clickable ? 'cursor-pointer' : 'cursor-not-allowed'
                  } ${CARD_STYLES[variant] || CARD_STYLES.locked}`;
                  // Số chương avatar — đổi màu theo trạng thái
                  const avatarCls =
                    variant === 'completed' ? 'bg-emerald-500 text-white shadow-sm' :
                    variant === 'next'      ? 'bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-md' :
                    variant === 'review'    ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md' :
                    'bg-slate-200 text-slate-500';

                  const inner = (
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-xl font-bold text-base flex items-center justify-center flex-shrink-0 ${avatarCls}`}>
                        {variant === 'completed' ? '✓' : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Hàng 1: title + badge */}
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <h2 className="font-bold text-slate-800 text-base leading-snug flex-1 min-w-0">
                            {m.title}
                          </h2>
                          <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${badge.cls}`}>
                            {badge.icon} {badge.label}
                          </span>
                        </div>
                        {/* Hàng 2: snippet body — clamp 2 dòng */}
                        {m.body_md && (
                          <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                            {snippet(m.body_md)}
                          </p>
                        )}
                        {/* Hàng 3: meta — done_sets badge */}
                        {m.done_sets > 0 && (
                          <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-white/70 border border-emerald-200 rounded text-[11px] text-emerald-700 font-medium">
                            🎯 Đã làm <strong>{m.done_sets}</strong> bộ kiểm tra
                          </div>
                        )}
                      </div>
                      {badge.clickable && (
                        <div className="text-slate-400 text-2xl self-center">→</div>
                      )}
                    </div>
                  );
                  return badge.clickable ? (
                    <Link key={m.id} to={`/noi-quy/module/${m.id}`} className={cardClass}>{inner}</Link>
                  ) : (
                    <div key={m.id} className={cardClass}>{inner}</div>
                  );
                })}
              </div>
            )}

            <div className="text-center text-xs text-slate-400 mt-6">
              Phiên bản nội quy v{list?.version_no || '—'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
